import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import LanguageSelector from '../common/LanguageSelector';
import './Header.css';

type DropdownKey = 'products' | 'openSource';

interface NavigationItem {
  path: string;
  label: string;
  isDropdown?: boolean;
  dropdownKey?: DropdownKey;
}

interface DropdownLink {
  path: string;
  label: string;
}

const Header: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [openDropdown, setOpenDropdown] = useState<DropdownKey | null>(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState<DropdownKey | null>(null);
  const [isHeaderHidden, setIsHeaderHidden] = useState<boolean>(false);
  const dropdownRefs = useRef<Partial<Record<DropdownKey, HTMLDivElement | null>>>({});

  const productItems: DropdownLink[] = [
    { path: '/products', label: t('products.title', 'Products') },
    { path: '/bomb-world-classic', label: t('products.bombWorldClassic.name', 'Bomb World: Classic') },
    { path: '/infinity-brain', label: t('products.infinityBrain.name', 'Infinity Brain Training') },
    { path: '/personal-prize', label: t('products.personalPrize.name', 'Personal Prize') },
    { path: '/personal-vault', label: t('products.personalVault.name', 'Personal Vault') },
    { path: '/personal-contact', label: t('products.personalContact.name', 'Personal Contact') },
    { path: '/wayhome', label: t('products.wayHome.name', 'Way Home') },
  ];

  const openSourceItems: DropdownLink[] = [
    { path: '/open-source', label: t('openSource.title', 'Open Source') },
    {
      path: '/open-source/activity-heatmap-calendar',
      label: t('openSource.packages.activityHeatmapCalendar.name', 'Activity Heatmap Calendar'),
    },
  ];

  const dropdownItems: Record<DropdownKey, DropdownLink[]> = {
    products: productItems,
    openSource: openSourceItems,
  };

  const navigationItems: NavigationItem[] = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/products', label: t('nav.products'), isDropdown: true, dropdownKey: 'products' },
    { path: '/open-source', label: t('nav.openSource'), isDropdown: true, dropdownKey: 'openSource' },
    { path: '/news', label: t('nav.news') },
    { path: '/contact', label: t('nav.contact') },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!openDropdown) {
        return;
      }
      const el = dropdownRefs.current[openDropdown];
      if (el && !el.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const toggleMobileMenu = (): void => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleHeaderVisibility = (): void => {
    setIsHeaderHidden(!isHeaderHidden);
  };

  const isProductPage = location.pathname.startsWith('/products') ||
                         location.pathname.startsWith('/bomb-world-classic') ||
                         location.pathname === '/infinity-brain' ||
                         location.pathname === '/personal-prize' ||
                         location.pathname === '/personal-vault' ||
                         location.pathname.startsWith('/personal-contact') ||
                         location.pathname === '/wayhome';

  const isOpenSourcePage = location.pathname.startsWith('/open-source');

  const isDropdownActive = (key?: DropdownKey): boolean => {
    if (key === 'products') return isProductPage;
    if (key === 'openSource') return isOpenSourcePage;
    return false;
  };

  const toggleDesktopDropdown = (key: DropdownKey) => {
    setOpenDropdown((current) => (current === key ? null : key));
  };

  const toggleMobileDropdown = (key: DropdownKey) => {
    setOpenMobileDropdown((current) => (current === key ? null : key));
  };

  return (
    <>
      <header className={`header ${isHeaderHidden ? 'header-hidden' : ''}`}>
      <div className="header-container">
        <div className="header-brand">
          <Link to="/" className="brand-link">
            <div className="brand-logo">
              <img 
                src="/assets/snorelab_icon.png" 
                alt={t('home.title') as string} 
                className="logo-icon"
              />
              <span className="logo-text">{t('home.title')}</span>
            </div>
          </Link>
        </div>

        <nav className="header-nav">
          <div className="nav-desktop">
            {navigationItems.map((item) => {
              if (item.isDropdown && item.dropdownKey) {
                const key = item.dropdownKey;
                return (
                  <div 
                    key={item.path}
                    className="nav-dropdown"
                    ref={(el) => {
                      dropdownRefs.current[key] = el;
                    }}
                  >
                    <button
                      className={`nav-link nav-dropdown-toggle ${isDropdownActive(key) ? 'active' : ''}`}
                      onClick={() => toggleDesktopDropdown(key)}
                    >
                      {item.label}
                      <span className="dropdown-arrow">▼</span>
                    </button>
                    {openDropdown === key && (
                      <div className="dropdown-menu">
                        {dropdownItems[key].map((entry) => (
                          <Link
                            key={entry.path}
                            to={entry.path}
                            className={`dropdown-item ${location.pathname === entry.path ? 'active' : ''}`}
                            onClick={() => setOpenDropdown(null)}
                          >
                            {entry.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>

          <div className="header-actions">
            <LanguageSelector />
            {/* Desktop header toggle button */}
            <button
              className="desktop-header-toggle"
              onClick={toggleHeaderVisibility}
              aria-label={isHeaderHidden ? 'Show header' : 'Hide header'}
            >
              {isHeaderHidden ? '▼' : '▲'}
            </button>
            <button
              className="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>
        </nav>

        <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
          {navigationItems.map((item) => {
            if (item.isDropdown && item.dropdownKey) {
              const key = item.dropdownKey;
              return (
                <div key={item.path} className="mobile-dropdown">
                  <button
                    className={`mobile-nav-link mobile-dropdown-toggle ${isDropdownActive(key) ? 'active' : ''}`}
                    onClick={() => toggleMobileDropdown(key)}
                  >
                    {item.label}
                    <span className="dropdown-arrow">▼</span>
                  </button>
                  {openMobileDropdown === key && (
                    <div className="mobile-dropdown-menu">
                      {dropdownItems[key].map((entry) => (
                        <Link
                          key={entry.path}
                          to={entry.path}
                          className={`mobile-dropdown-item ${location.pathname === entry.path ? 'active' : ''}`}
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            setOpenMobileDropdown(null);
                          }}
                        >
                          {entry.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`mobile-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>
    </header>

    {/* Mobile Header Toggle Button */}
    <button 
      className={`mobile-header-toggle ${isHeaderHidden ? 'header-toggle-show' : ''}`}
      onClick={toggleHeaderVisibility}
      aria-label={isHeaderHidden ? 'Show header' : 'Hide header'}
      title={isHeaderHidden ? 'Show header' : 'Hide header'}
    >
      {isHeaderHidden ? '▼' : '▲'}
    </button>
    
    {/* Desktop Header Toggle Button - always fixed position */}
    <button 
      className="desktop-header-toggle-fixed"
      onClick={toggleHeaderVisibility}
      aria-label={isHeaderHidden ? 'Show header' : 'Hide header'}
      title={isHeaderHidden ? 'Show header' : 'Hide header'}
    >
      {isHeaderHidden ? '▼' : '▲'}
    </button>
    </>
  );
};

export default Header;
